import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CommentsApi,
  type GroupedCommentsDto,
  type EntityCommentGroupDto,
  type CommentDto,
} from '@ata-digital/client-tax-system-api-client';
import { createApiConfig, apiAxiosInstance } from '@/lib/api/config';
import { useAuthState } from '@/hooks/auth/useAuth';
import { taxBinderKeys, dashboardStatsKeys } from '@/lib/query/options';

// Re-export types for convenience
export type { GroupedCommentsDto, EntityCommentGroupDto, CommentDto };

export interface GroupedCommentsFilters {
  binderId: string;
  includeResolved?: boolean;
  sortBy?: 'latestComment' | 'entityName' | 'unreadFirst';
}

export const groupedCommentKeys = {
  all: ['comments', 'grouped'] as const,
  list: (binderId: string) => [...groupedCommentKeys.all, binderId] as const,
  filtered: (filters: GroupedCommentsFilters) => [...groupedCommentKeys.all, filters] as const,
} as const;

/**
 * Hook to fetch comments grouped by their parent entity (document, document request, or binder-level)
 * Returns comments organized with entity context for display in unified comments panel
 */
export function useGroupedComments(binderId: string, options?: Omit<GroupedCommentsFilters, 'binderId'>) {
  const { data: authState } = useAuthState();
  const commentsApi = new CommentsApi(createApiConfig(authState?.token || undefined), undefined, apiAxiosInstance);

  const filters: GroupedCommentsFilters = {
    binderId,
    includeResolved: options?.includeResolved ?? true,
    sortBy: options?.sortBy ?? 'latestComment',
  };

  return useQuery({
    queryKey: groupedCommentKeys.filtered(filters),
    queryFn: async (): Promise<GroupedCommentsDto> => {
      const response = await commentsApi.apiCommentsGroupedGet({
        binderId: filters.binderId,
        includeResolved: filters.includeResolved,
        sortBy: filters.sortBy,
      });

      // API returns { success: true, data: GroupedCommentsDto }
      return response.data.data!;
    },
    enabled: !!authState?.isAuthenticated && !!binderId,
    staleTime: 1000 * 30, // 30 seconds (conversational data)
    refetchOnWindowFocus: true,
  });
}

/**
 * Hook to fetch grouped comments with real-time polling
 * Use this when the comments panel is actively visible
 */
export function useGroupedCommentsRealtime(
  binderId: string,
  options?: Omit<GroupedCommentsFilters, 'binderId'> & { enabled?: boolean }
) {
  const { data: authState } = useAuthState();
  const commentsApi = new CommentsApi(createApiConfig(authState?.token || undefined), undefined, apiAxiosInstance);

  const filters: GroupedCommentsFilters = {
    binderId,
    includeResolved: options?.includeResolved ?? true,
    sortBy: options?.sortBy ?? 'latestComment',
  };

  return useQuery({
    queryKey: groupedCommentKeys.filtered(filters),
    queryFn: async (): Promise<GroupedCommentsDto> => {
      const response = await commentsApi.apiCommentsGroupedGet({
        binderId: filters.binderId,
        includeResolved: filters.includeResolved,
        sortBy: filters.sortBy,
      });

      return response.data.data!;
    },
    enabled: (options?.enabled ?? true) && !!authState?.isAuthenticated && !!binderId,
    staleTime: 0, // Always consider stale for real-time
    refetchInterval: 300000, // Poll every 5 min (safety net — SignalR handles real-time)
    refetchIntervalInBackground: false, // Only when tab is active
  });
}

/**
 * Hook to invalidate grouped comments cache
 * Call this after creating, updating, or deleting a comment
 */
export function useInvalidateGroupedComments() {
  const queryClient = useQueryClient();

  return {
    invalidate: (binderId?: string) => {
      if (binderId) {
        queryClient.invalidateQueries({ queryKey: groupedCommentKeys.list(binderId) });
      } else {
        queryClient.invalidateQueries({ queryKey: groupedCommentKeys.all });
      }
    },
    invalidateAll: () => {
      queryClient.invalidateQueries({ queryKey: groupedCommentKeys.all });
    },
    /**
     * Invalidate dashboard-related queries when comment read status changes
     * This updates the "Comments to Review" stat and binder row badges
     */
    invalidateDashboard: () => {
      // Invalidate dashboard stats (for "Comments to Review" stat card)
      queryClient.invalidateQueries({ queryKey: dashboardStatsKeys.stats() });
      // Invalidate binder lists (for unread comment badges on binder rows)
      queryClient.invalidateQueries({ queryKey: taxBinderKeys.lists() });
    },
  };
}

/**
 * Hook to optimistically update comment read status in the cache
 * Returns the previous data for rollback on error
 */
export function useOptimisticCommentUpdate() {
  const queryClient = useQueryClient();

  const updateCommentReadStatus = (commentId: string, isRead: boolean): GroupedCommentsDto | undefined => {
    // Find all queries that might contain this comment
    const queries = queryClient.getQueriesData<GroupedCommentsDto>({
      queryKey: groupedCommentKeys.all,
    });

    let previousData: GroupedCommentsDto | undefined;

    for (const [queryKey, data] of queries) {
      if (!data) continue;

      // Helper to update comment in an array
      const updateComments = (comments: CommentDto[]): boolean => {
        let updated = false;
        for (const comment of comments) {
          if (comment.id === commentId) {
            comment.isRead = isRead;
            updated = true;
          }
          // Also check replies
          if (comment.replies) {
            for (const reply of comment.replies) {
              if (reply.id === commentId) {
                reply.isRead = isRead;
                updated = true;
              }
            }
          }
        }
        return updated;
      };

      // Helper to update counts in a group
      const recalculateGroupCounts = (group: EntityCommentGroupDto) => {
        const allComments = [...(group.comments || []), ...(group.comments || []).flatMap((c) => c.replies || [])];
        group.unreadCount = allComments.filter((c) => !c.isRead).length;
      };

      // Clone the data for immutability
      const newData: GroupedCommentsDto = JSON.parse(JSON.stringify(data));
      let foundComment = false;

      // Update binder comments
      if (newData.binderComments && updateComments(newData.binderComments)) {
        foundComment = true;
      }

      // Update document groups
      if (newData.documentGroups) {
        for (const group of newData.documentGroups) {
          if (group.comments && updateComments(group.comments)) {
            recalculateGroupCounts(group);
            foundComment = true;
          }
        }
      }

      // Update document request groups
      if (newData.documentRequestGroups) {
        for (const group of newData.documentRequestGroups) {
          if (group.comments && updateComments(group.comments)) {
            recalculateGroupCounts(group);
            foundComment = true;
          }
        }
      }

      if (foundComment) {
        // Recalculate total unread
        const allComments = [
          ...(newData.binderComments || []),
          ...(newData.binderComments || []).flatMap((c) => c.replies || []),
          ...(newData.documentGroups || []).flatMap((g) => g.comments || []),
          ...(newData.documentGroups || []).flatMap((g) => (g.comments || []).flatMap((c) => c.replies || [])),
          ...(newData.documentRequestGroups || []).flatMap((g) => g.comments || []),
          ...(newData.documentRequestGroups || []).flatMap((g) => (g.comments || []).flatMap((c) => c.replies || [])),
        ];
        newData.unreadComments = allComments.filter((c) => !c.isRead).length;

        // Store previous data for rollback (only once)
        if (!previousData) {
          previousData = data;
        }

        // Update the cache
        queryClient.setQueryData(queryKey, newData);
      }
    }

    return previousData;
  };

  const rollback = (previousData: GroupedCommentsDto | undefined, queryKey: readonly unknown[]) => {
    if (previousData) {
      queryClient.setQueryData(queryKey, previousData);
    }
  };

  /**
   * Optimistically add a new comment to the cache
   * Returns the previous data for rollback on error
   */
  const addCommentOptimistically = (
    newComment: {
      content: string;
      binderId?: string;
      documentId?: string;
      documentRequestId?: string;
      isInternal?: boolean;
    },
    userInfo: {
      userId: string;
      userName: string;
      userRole: 'preparer' | 'admin' | 'client';
    }
  ): { previousData: GroupedCommentsDto | undefined; tempId: string } => {
    const queries = queryClient.getQueriesData<GroupedCommentsDto>({
      queryKey: groupedCommentKeys.all,
    });

    let previousData: GroupedCommentsDto | undefined;
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;

    // Create the temporary comment
    const tempComment: CommentDto = {
      id: tempId,
      content: newComment.content,
      binderId: newComment.binderId,
      documentId: newComment.documentId,
      documentRequestId: newComment.documentRequestId,
      isInternal: newComment.isInternal ?? false,
      createdById: userInfo.userId,
      createdByName: userInfo.userName,
      createdByRole: userInfo.userRole,
      createdAt: new Date().toISOString(),
      isRead: true, // Own comments are always read
      replies: [],
    };

    for (const [queryKey, data] of queries) {
      if (!data) continue;

      // Clone the data for immutability
      const newData: GroupedCommentsDto = JSON.parse(JSON.stringify(data));

      // Determine where to add the comment
      if (newComment.documentId) {
        // Add to document group
        const group = newData.documentGroups?.find((g) => g.entityId === newComment.documentId);
        if (group) {
          group.comments = [...(group.comments || []), tempComment];
          group.totalCount = (group.totalCount || 0) + 1;
          group.latestCommentAt = tempComment.createdAt;
        }
      } else if (newComment.documentRequestId) {
        // Add to document request group
        const group = newData.documentRequestGroups?.find((g) => g.entityId === newComment.documentRequestId);
        if (group) {
          group.comments = [...(group.comments || []), tempComment];
          group.totalCount = (group.totalCount || 0) + 1;
          group.latestCommentAt = tempComment.createdAt;
        }
      } else {
        // Add to binder comments
        newData.binderComments = [...(newData.binderComments || []), tempComment];
      }

      // Update total count
      newData.totalComments = (newData.totalComments || 0) + 1;

      // Store previous data for rollback (only once)
      if (!previousData) {
        previousData = data;
      }

      // Update the cache
      queryClient.setQueryData(queryKey, newData);
    }

    return { previousData, tempId };
  };

  /**
   * Remove a temporarily added comment (for rollback or after server confirms)
   */
  const removeOptimisticComment = (tempId: string) => {
    const queries = queryClient.getQueriesData<GroupedCommentsDto>({
      queryKey: groupedCommentKeys.all,
    });

    for (const [queryKey, data] of queries) {
      if (!data) continue;

      const newData: GroupedCommentsDto = JSON.parse(JSON.stringify(data));
      let removed = false;

      // Remove from binder comments
      if (newData.binderComments) {
        const idx = newData.binderComments.findIndex((c) => c.id === tempId);
        if (idx !== -1) {
          newData.binderComments.splice(idx, 1);
          removed = true;
        }
      }

      // Remove from document groups
      if (!removed && newData.documentGroups) {
        for (const group of newData.documentGroups) {
          if (group.comments) {
            const idx = group.comments.findIndex((c) => c.id === tempId);
            if (idx !== -1) {
              group.comments.splice(idx, 1);
              group.totalCount = Math.max(0, (group.totalCount || 1) - 1);
              removed = true;
              break;
            }
          }
        }
      }

      // Remove from document request groups
      if (!removed && newData.documentRequestGroups) {
        for (const group of newData.documentRequestGroups) {
          if (group.comments) {
            const idx = group.comments.findIndex((c) => c.id === tempId);
            if (idx !== -1) {
              group.comments.splice(idx, 1);
              group.totalCount = Math.max(0, (group.totalCount || 1) - 1);
              removed = true;
              break;
            }
          }
        }
      }

      if (removed) {
        newData.totalComments = Math.max(0, (newData.totalComments || 1) - 1);
        queryClient.setQueryData(queryKey, newData);
      }
    }
  };

  /**
   * Optimistically update a comment's content
   * Returns the previous content for rollback on error
   */
  const updateCommentContentOptimistically = (
    commentId: string,
    newContent: string
  ): { previousContent: string | undefined } => {
    const queries = queryClient.getQueriesData<GroupedCommentsDto>({
      queryKey: groupedCommentKeys.all,
    });

    let previousContent: string | undefined;

    for (const [queryKey, data] of queries) {
      if (!data) continue;

      // Clone the data for immutability
      const newData: GroupedCommentsDto = JSON.parse(JSON.stringify(data));
      let found = false;

      // Helper to update comment content in an array
      const updateContent = (comments: CommentDto[]): boolean => {
        for (const comment of comments) {
          if (comment.id === commentId) {
            if (previousContent === undefined) {
              previousContent = comment.content ?? undefined;
            }
            comment.content = newContent;
            comment.updatedAt = new Date().toISOString();
            return true;
          }
          // Also check replies
          if (comment.replies) {
            for (const reply of comment.replies) {
              if (reply.id === commentId) {
                if (previousContent === undefined) {
                  previousContent = reply.content ?? undefined;
                }
                reply.content = newContent;
                reply.updatedAt = new Date().toISOString();
                return true;
              }
            }
          }
        }
        return false;
      };

      // Update in binder comments
      if (newData.binderComments && updateContent(newData.binderComments)) {
        found = true;
      }

      // Update in document groups
      if (!found && newData.documentGroups) {
        for (const group of newData.documentGroups) {
          if (group.comments && updateContent(group.comments)) {
            found = true;
            break;
          }
        }
      }

      // Update in document request groups
      if (!found && newData.documentRequestGroups) {
        for (const group of newData.documentRequestGroups) {
          if (group.comments && updateContent(group.comments)) {
            found = true;
            break;
          }
        }
      }

      if (found) {
        queryClient.setQueryData(queryKey, newData);
      }
    }

    return { previousContent };
  };

  /**
   * Rollback a comment content update
   */
  const rollbackCommentContent = (commentId: string, previousContent: string) => {
    updateCommentContentOptimistically(commentId, previousContent);
  };

  /**
   * Optimistically delete a comment from the cache
   * Returns the deleted comment data for rollback on error
   */
  const deleteCommentOptimistically = (
    commentId: string
  ): { deletedComment: CommentDto | undefined; parentCommentId?: string } => {
    const queries = queryClient.getQueriesData<GroupedCommentsDto>({
      queryKey: groupedCommentKeys.all,
    });

    let deletedComment: CommentDto | undefined;
    let parentCommentId: string | undefined;

    for (const [queryKey, data] of queries) {
      if (!data) continue;

      const newData: GroupedCommentsDto = JSON.parse(JSON.stringify(data));
      let deleted = false;

      // Helper to recalculate group counts
      const recalculateGroupCounts = (group: EntityCommentGroupDto) => {
        const allComments = [...(group.comments || []), ...(group.comments || []).flatMap((c) => c.replies || [])];
        group.totalCount = allComments.length;
        group.unreadCount = allComments.filter((c) => !c.isRead).length;
      };

      // Helper to delete comment from an array (handles both top-level and replies)
      const deleteFromComments = (comments: CommentDto[]): boolean => {
        // First check top-level comments
        const idx = comments.findIndex((c) => c.id === commentId);
        if (idx !== -1) {
          if (!deletedComment) {
            deletedComment = comments[idx];
          }
          comments.splice(idx, 1);
          return true;
        }

        // Then check replies
        for (const comment of comments) {
          if (comment.replies) {
            const replyIdx = comment.replies.findIndex((r) => r.id === commentId);
            if (replyIdx !== -1) {
              if (!deletedComment) {
                deletedComment = comment.replies[replyIdx];
                parentCommentId = comment.id;
              }
              comment.replies.splice(replyIdx, 1);
              return true;
            }
          }
        }
        return false;
      };

      // Delete from binder comments
      if (newData.binderComments && deleteFromComments(newData.binderComments)) {
        deleted = true;
      }

      // Delete from document groups
      if (!deleted && newData.documentGroups) {
        for (const group of newData.documentGroups) {
          if (group.comments && deleteFromComments(group.comments)) {
            recalculateGroupCounts(group);
            deleted = true;
            break;
          }
        }
      }

      // Delete from document request groups
      if (!deleted && newData.documentRequestGroups) {
        for (const group of newData.documentRequestGroups) {
          if (group.comments && deleteFromComments(group.comments)) {
            recalculateGroupCounts(group);
            deleted = true;
            break;
          }
        }
      }

      if (deleted) {
        // Recalculate totals
        const allComments = [
          ...(newData.binderComments || []),
          ...(newData.binderComments || []).flatMap((c) => c.replies || []),
          ...(newData.documentGroups || []).flatMap((g) => g.comments || []),
          ...(newData.documentGroups || []).flatMap((g) => (g.comments || []).flatMap((c) => c.replies || [])),
          ...(newData.documentRequestGroups || []).flatMap((g) => g.comments || []),
          ...(newData.documentRequestGroups || []).flatMap((g) => (g.comments || []).flatMap((c) => c.replies || [])),
        ];
        newData.totalComments = allComments.length;
        newData.unreadComments = allComments.filter((c) => !c.isRead).length;

        queryClient.setQueryData(queryKey, newData);
      }
    }

    return { deletedComment, parentCommentId };
  };

  /**
   * Rollback a comment deletion by re-adding it to the cache
   * Note: This is a simplified rollback - for complex cases, invalidate instead
   */
  const rollbackCommentDeletion = (deletedComment: CommentDto, parentCommentId?: string) => {
    const queries = queryClient.getQueriesData<GroupedCommentsDto>({
      queryKey: groupedCommentKeys.all,
    });

    for (const [queryKey, data] of queries) {
      if (!data) continue;

      const newData: GroupedCommentsDto = JSON.parse(JSON.stringify(data));
      let restored = false;

      // Helper to recalculate group counts
      const recalculateGroupCounts = (group: EntityCommentGroupDto) => {
        const allComments = [...(group.comments || []), ...(group.comments || []).flatMap((c) => c.replies || [])];
        group.totalCount = allComments.length;
        group.unreadCount = allComments.filter((c) => !c.isRead).length;
      };

      // If it was a reply, add it back to the parent comment
      if (parentCommentId) {
        const addToParent = (comments: CommentDto[]): boolean => {
          for (const comment of comments) {
            if (comment.id === parentCommentId) {
              comment.replies = [...(comment.replies || []), deletedComment];
              return true;
            }
          }
          return false;
        };

        if (newData.binderComments && addToParent(newData.binderComments)) {
          restored = true;
        }
        if (!restored && newData.documentGroups) {
          for (const group of newData.documentGroups) {
            if (group.comments && addToParent(group.comments)) {
              recalculateGroupCounts(group);
              restored = true;
              break;
            }
          }
        }
        if (!restored && newData.documentRequestGroups) {
          for (const group of newData.documentRequestGroups) {
            if (group.comments && addToParent(group.comments)) {
              recalculateGroupCounts(group);
              restored = true;
              break;
            }
          }
        }
      } else {
        // It was a top-level comment, add it back based on its entity associations
        if (deletedComment.documentId) {
          const group = newData.documentGroups?.find((g) => g.entityId === deletedComment.documentId);
          if (group) {
            group.comments = [...(group.comments || []), deletedComment];
            recalculateGroupCounts(group);
            restored = true;
          }
        } else if (deletedComment.documentRequestId) {
          const group = newData.documentRequestGroups?.find((g) => g.entityId === deletedComment.documentRequestId);
          if (group) {
            group.comments = [...(group.comments || []), deletedComment];
            recalculateGroupCounts(group);
            restored = true;
          }
        } else {
          // Binder-level comment
          newData.binderComments = [...(newData.binderComments || []), deletedComment];
          restored = true;
        }
      }

      if (restored) {
        // Recalculate totals
        const allComments = [
          ...(newData.binderComments || []),
          ...(newData.binderComments || []).flatMap((c) => c.replies || []),
          ...(newData.documentGroups || []).flatMap((g) => g.comments || []),
          ...(newData.documentGroups || []).flatMap((g) => (g.comments || []).flatMap((c) => c.replies || [])),
          ...(newData.documentRequestGroups || []).flatMap((g) => g.comments || []),
          ...(newData.documentRequestGroups || []).flatMap((g) => (g.comments || []).flatMap((c) => c.replies || [])),
        ];
        newData.totalComments = allComments.length;
        newData.unreadComments = allComments.filter((c) => !c.isRead).length;

        queryClient.setQueryData(queryKey, newData);
      }
    }
  };

  return {
    updateCommentReadStatus,
    rollback,
    addCommentOptimistically,
    removeOptimisticComment,
    updateCommentContentOptimistically,
    rollbackCommentContent,
    deleteCommentOptimistically,
    rollbackCommentDeletion,
  };
}
